import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Achievement } from '@/utils/storage';
import { useStarData } from '@/hooks/useStarData';

interface EditAchievementForm {
  title: string;
  description: string;
  requiredStars: number;
  linkedTaskId: string;
  requiredCompletions: number;
  secret: boolean;
}

const EditAchievementPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { data, updateData, loading } = useStarData();

  const fromEditMode = location.state?.fromEditMode === true;
  const isTaskBased = location.state?.isTaskBased === true;
  
  const [editForm, setEditForm] = useState<EditAchievementForm>({
    title: '',
    description: '',
    requiredStars: 50,
    linkedTaskId: '',
    requiredCompletions: 5,
    secret: false
  });
  
  const isAddingNew = id === 'new';
  
  const navigateBack = useCallback(() => {
    navigate('/', { 
      state: { 
        keepEditMode: fromEditMode,
        initialTab: 'trophies' 
      } 
    });
  }, [fromEditMode, navigate]);

  useEffect(() => {
    if (!data) return;
    
    if (!isAddingNew) {
      const achievement = data.achievements.find(a => a.id === id);
      if (achievement) {
        setEditForm({
          title: achievement.title,
          description: achievement.description || '',
          requiredStars: achievement.requiredStars || 50,
          linkedTaskId: achievement.linkedTaskId || '',
          requiredCompletions: achievement.requiredCompletions || 5,
          secret: achievement.secret || false
        });
      } else {
        toast.error('Trophy not found');
        navigateBack();
      }
    } else if (isTaskBased) {
      // Default to task-based settings when creating a new task trophy
      setEditForm(prev => ({
        ...prev,
        linkedTaskId: data.tasks.length > 0 ? data.tasks[0].id : '',
        requiredCompletions: 5
      }));
    }
  }, [id, isAddingNew, isTaskBased, data, navigateBack]);
  
  const handleSaveEdit = () => {
    if (!data) {
      toast.error("Data has not loaded yet.");
      return;
    }

    if (!editForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (editForm.linkedTaskId && (!editForm.requiredCompletions || editForm.requiredCompletions < 1)) {
      toast.error('Required completions must be at least 1');
      return;
    }
    
    let updatedAchievements: Achievement[];
    
    if (isAddingNew) {
      const newAchievement: Achievement = {
        id: Date.now().toString(),
        title: editForm.title,
        description: editForm.description || undefined,
        secret: editForm.secret,
        unlocked: false,
        date: undefined
      };
      
      if (editForm.linkedTaskId) {
        newAchievement.linkedTaskId = editForm.linkedTaskId;
        newAchievement.requiredCompletions = editForm.requiredCompletions;
      } else {
        newAchievement.requiredStars = editForm.requiredStars;
      }
      
      updatedAchievements = [...data.achievements, newAchievement];
      toast.success('New trophy added!');
    } else {
      const achievementIndex = data.achievements.findIndex(a => a.id === id);
      if (achievementIndex >= 0) {
        const oldAchievement = data.achievements[achievementIndex];
        const updatedAchievement: Achievement = {
          ...oldAchievement,
          title: editForm.title,
          description: editForm.description || undefined,
          secret: editForm.secret
        };
        
        if (editForm.linkedTaskId) {
          updatedAchievement.linkedTaskId = editForm.linkedTaskId;
          updatedAchievement.requiredCompletions = editForm.requiredCompletions;
          delete updatedAchievement.requiredStars;
        } else {
          updatedAchievement.requiredStars = editForm.requiredStars;
          delete updatedAchievement.linkedTaskId;
          delete updatedAchievement.requiredCompletions;
        }
        
        updatedAchievements = [...data.achievements];
        updatedAchievements[achievementIndex] = updatedAchievement;
        toast.success('Trophy updated!');
      } else {
        toast.error('Could not find the trophy to update.');
        navigateBack();
        return;
      }
    }
    
    updateData({ ...data, achievements: updatedAchievements });
    navigateBack();
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[100dvh]">
        <p>Loading...</p>
      </div>
    );
  }
  
  return (
    <div className="min-h-[100dvh] p-4 sm:p-6 pt-[calc(1rem+env(safe-area-inset-top,0px))] pb-[calc(1rem+env(safe-area-inset-bottom,0px))] pl-[calc(1rem+env(safe-area-inset-left,0px))] pr-[calc(1rem+env(safe-area-inset-right,0px))]">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={navigateBack}
            className="mr-2"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Trophy size={24} />
            {isAddingNew ? 'Add New Trophy' : 'Edit Trophy'}
          </h1>
        </div>
        
        <div className="space-y-6 bg-card rounded-xl p-6 shadow-md">
          <div className="space-y-3">
            <Label htmlFor="title">Trophy Title</Label>
            <Input
              id="title"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              placeholder="Enter title (e.g. Math Master)"
            />
          </div>
          
          <div className="space-y-3">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              placeholder="Enter description (e.g. Earned by mastering math tasks)"
              rows={3}
            />
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="secret">Secret Trophy</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Secret trophies show a hidden icon instead of the regular trophy icon
                </p>
              </div>
              <Switch
                id="secret"
                checked={editForm.secret}
                onCheckedChange={(checked) => setEditForm({ ...editForm, secret: checked })}
              />
            </div>
          </div>
          
          <div className="flex flex-col space-y-2 border-t border-border pt-4">
            <label className="font-medium">Trophy Type</label>
            <div className="grid grid-cols-2 gap-4">
              <div 
                className={`p-4 border rounded-lg cursor-pointer flex flex-col items-center text-center ${!editForm.linkedTaskId ? 'bg-primary/10 border-primary' : 'hover:bg-accent'}`}
                onClick={() => setEditForm(prev => ({...prev, linkedTaskId: ''}))}
              >
                <div className="text-2xl mb-2">⭐</div>
                <div className="font-medium">Star-based Trophy</div>
                <div className="text-xs text-muted-foreground mt-1">Awarded when total stars reach a threshold</div>
              </div>
              
              <div 
                className={`p-4 border rounded-lg cursor-pointer flex flex-col items-center text-center ${editForm.linkedTaskId ? 'bg-primary/10 border-primary' : 'hover:bg-accent'}`}
                onClick={() => {
                  if (!data) return;
                  setEditForm(prev => ({
                    ...prev, 
                    linkedTaskId: data.tasks.length > 0 ? data.tasks[0].id : ''
                  }));
                }}
              >
                <div className="text-2xl mb-2">✓</div>
                <div className="font-medium">Task-based Trophy</div>
                <div className="text-xs text-muted-foreground mt-1">Awarded when a specific task is completed multiple times</div>
              </div>
            </div>
          </div>
          
          {editForm.linkedTaskId ? (
            <>
              <div className="space-y-3">
                <Label htmlFor="linkedTaskId">Select Task</Label>
                <Select
                  value={editForm.linkedTaskId}
                  onValueChange={(value) => setEditForm({ ...editForm, linkedTaskId: value })}
                >
                  <SelectTrigger id="linkedTaskId">
                    <SelectValue placeholder="Select a task" />
                  </SelectTrigger>
                  <SelectContent>
                    {data.tasks.map(task => (
                      <SelectItem key={task.id} value={task.id}>
                        {task.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-3">
                <Label htmlFor="requiredCompletions">Required Completions</Label>
                <Input
                  id="requiredCompletions"
                  type="number"
                  min="1"
                  value={editForm.requiredCompletions}
                  onChange={(e) => setEditForm({ 
                    ...editForm, 
                    requiredCompletions: parseInt(e.target.value) || 1 
                  })}
                />
                <p className="text-xs text-muted-foreground">
                  How many times the task needs to be completed to earn this trophy
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <Label htmlFor="requiredStars">Required Stars</Label>
              <Input
                id="requiredStars"
                type="number"
                min="1"
                value={editForm.requiredStars}
                onChange={(e) => setEditForm({ 
                  ...editForm, 
                  requiredStars: parseInt(e.target.value) || 1 
                })}
              />
              <p className="text-xs text-muted-foreground">
                How many total stars are needed to earn this trophy
              </p>
            </div>
          )}
          
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={navigateBack} className="mr-2">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="flex items-center gap-1">
              <Check size={16} />
              Save Trophy
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAchievementPage;
